import React, { useCallback, useEffect, useState } from 'react';
import { Users, CheckCircle2, AlertTriangle, Loader2, CalendarOff, Activity, Check, X, Info, RefreshCw, PenLine, ShieldOff, ShieldCheck, Camera, Receipt } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { adminGet, adminPost, reportError, formatDate, BACKEND_URL } from './adminApi';
import { AdminTeamHours } from './AdminTeamHours';
import { AdminPointageAlerts } from './AdminPointageAlerts';
import { AdminEaserNotifications } from './AdminEaserNotifications';
import { AdminEaserMoral } from './AdminEaserMoral';
import { AdminEaserRequests } from './AdminEaserRequests';
import { AdminPhotoRulesCard } from './AdminPhotoRulesCard';
import { LeaveStatusBadge } from '../easer/EaserLeaves';
import { tagStyle } from '../../lib/jobTags';

const KIND_LABELS = {
  pointage_start: 'Début intervention pointé',
  pointage_end: 'Fin intervention pointée',
  intervention_terminee: 'Intervention terminée (signature)',
  journee_start: 'Début de journée',
  journee_end: 'Fin de journée',
  conges_demande: 'Demande de congés',
  tract_start: 'Distribution de tracts démarrée',
  tract_stop: 'Distribution de tracts clôturée',
  compte_rendu: 'Compte-rendu + photos',
  incident: 'Imprévu signalé',
  note_de_frais: 'Note de frais envoyée',
};
const INCIDENT_LABELS = { late: 'En retard', absent: 'Client absent', access: "Impossible d'accéder", other: 'Autre imprévu' };

/** Admin tab: Dolibarr connection, employee web passwords, leave requests and activity log. */

export function AdminEasersTab({ adminKey }) {
  const [overview, setOverview] = useState(null);
  const [leaves, setLeaves] = useState(null);
  const [activity, setActivity] = useState(null);
  const [reports, setReports] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [activityFilter, setActivityFilter] = useState('all');
  const [leaveFilter, setLeaveFilter] = useState('pending');
  const [decision, setDecision] = useState(null);
  const [accessBusy, setAccessBusy] = useState(null);

  const toggleAccess = async (emp) => {
    const revoke = !emp.revoked;
    if (revoke && !window.confirm(`Couper immédiatement l'accès à l'espace Easer de ${emp.name} ? Ses sessions en cours (site et Dolibarr) seront déconnectées.`)) return;
    setAccessBusy(emp.login);
    try {
      await adminPost(revoke ? '/api/admin/easer/revoke' : '/api/admin/easer/restore', adminKey, {}, { login: emp.login });
      toast.success(revoke ? `Accès de ${emp.name} révoqué` : `Accès de ${emp.name} rétabli`);
      await load();
    } catch (err) {
      reportError('accès Easer', err, (m) => toast.error(m || 'Échec'));
    } finally {
      setAccessBusy(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const [ov, lv, ac, rp] = await Promise.all([
        adminGet('/api/admin/easer/overview', adminKey),
        adminGet('/api/admin/easer/leaves', adminKey, leaveFilter === 'all' ? {} : { status: leaveFilter }),
        adminGet('/api/admin/easer/activity', adminKey, { days: 14 }),
        adminGet('/api/admin/easer/reports', adminKey, { days: 30 }),
      ]);
      setOverview(ov.data);
      setLeaves(lv.data.items);
      setActivity(ac.data.items);
      setReports(rp.data.items);
      adminGet('/api/admin/easer/expenses', adminKey, { days: 90 }).then((ex) => setExpenses(ex.data.items)).catch(() => setExpenses([]));
    } catch (err) {
      reportError('espace Easer', err, () => toast.error("Impossible de charger l'espace Easer"));
      setOverview((o) => o || { dolibarr: { ok: false, message: 'Erreur de chargement' }, employees: [] });
      setLeaves((l) => l || []);
      setActivity((a) => a || []);
      setReports((r) => r || []);
    }
  }, [adminKey, leaveFilter]);

  useEffect(() => { load(); }, [load]);

  if (!overview) return <div className="space-y-4"><Skeleton className="h-24 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>;

  const dol = overview.dolibarr || {};
  const filteredActivity = (activity || []).filter((a) => activityFilter === 'all'
    || (activityFilter === 'pointage' ? a.kind.startsWith('pointage') || a.kind.startsWith('journee') : a.kind === activityFilter));

  return (
    <div className="space-y-6" data-testid="admin-easers-tab">
      {/* Connexion */}
      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${dol.ok ? 'bg-[hsl(var(--brand-mint))]' : 'bg-destructive/10'}`}>
            {dol.ok ? <CheckCircle2 className="h-5 w-5 text-[hsl(var(--primary))]" /> : <AlertTriangle className="h-5 w-5 text-destructive" />}
          </div>
          <div className="flex-1 min-w-[200px]">
            <h3 className="font-['Space_Grotesk'] font-semibold">Connexion Dolibarr</h3>
            <p className="text-sm text-muted-foreground" data-testid="dolibarr-status">
              {dol.ok ? `Connecté · Dolibarr ${dol.version} · ${dol.url}` : (dol.message || 'Non connecté')}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="easers-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Actualiser</Button>
        </div>
        <div className="mt-4 flex items-start gap-2 rounded-lg bg-[hsl(var(--secondary))]/70 p-3 text-sm" data-testid="login-api-notice">
          <Info className="h-4 w-4 mt-0.5 text-[hsl(var(--primary))] flex-shrink-0" />
          <p>
            Les Easers se connectent à l'espace Easer avec <strong>leur identifiant et mot de passe Dolibarr</strong>. Le site ouvre alors une session Dolibarr à leur nom
            (gardée active pendant la journée, aucun mot de passe conservé) et appuie sur les boutons <strong>Début / Fin PRESTA</strong> à leur place :
            les pointages alimentent ainsi <strong>MySchedule</strong>. Sans session active, le pointage est créé par l'API avec un avertissement.
          </p>
        </div>
      </Card>

      {/* Demandes GRH */}
      <AdminEaserRequests adminKey={adminKey} />

      {/* Météo du moral */}
      <AdminEaserMoral adminKey={adminKey} />

      {/* Notifications aux Easers */}
      <AdminEaserNotifications adminKey={adminKey} employees={overview.employees || []} jobTags={overview.job_tags || []} />

      {/* Photos avant / après au pointage */}
      <AdminPhotoRulesCard adminKey={adminKey} />

      {/* Congés */}
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2"><CalendarOff className="h-4 w-4" /> Demandes de congés {overview.pending_leaves > 0 && <Badge className="bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]">{overview.pending_leaves} en attente</Badge>}</h3>
          <div className="flex gap-1">
            {[['pending', 'En attente'], ['approved', 'Acceptées'], ['refused', 'Refusées'], ['all', 'Toutes']].map(([id, label]) => (
              <Button key={id} size="sm" variant={leaveFilter === id ? 'default' : 'outline'} onClick={() => setLeaveFilter(id)} data-testid={`leaves-filter-${id}`}>{label}</Button>
            ))}
          </div>
        </div>
        {leaves === null ? <Skeleton className="h-16" /> : leaves.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="admin-leaves-empty">Aucune demande dans cette catégorie.</p>
        ) : (
          <ul className="divide-y divide-border" data-testid="admin-leaves-list">
            {leaves.map((l) => (
              <li key={l.id} className="py-3 flex flex-wrap items-center gap-3" data-testid="admin-leave-item">
                <div className="flex-1 min-w-[220px]">
                  <p className="font-medium">{l.user_name} <span className="text-muted-foreground font-normal">· {l.type_label}</span></p>
                  <p className="text-sm text-muted-foreground">Du {formatDate(l.start)} au {formatDate(l.end)} · demandé le {formatDate(l.created_at)}{l.reason ? ` · « ${l.reason} »` : ''}</p>
                  {l.decision_note && <p className="text-sm">Réponse : {l.decision_note}</p>}
                </div>
                <LeaveStatusBadge status={l.status} />
                {l.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-[hsl(var(--brand-sage))] hover:bg-[hsl(var(--brand-sage))]/90 text-white" onClick={() => setDecision({ leave: l, status: 'approved', note: '' })} data-testid="leave-approve"><Check className="h-4 w-4 mr-1" /> Accepter</Button>
                    <Button size="sm" variant="outline" onClick={() => setDecision({ leave: l, status: 'refused', note: '' })} data-testid="leave-refuse"><X className="h-4 w-4 mr-1" /> Refuser</Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Employés */}
      <Card className="p-5">
        <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2 mb-1"><Users className="h-4 w-4" /> Employés Dolibarr ({overview.employees.length})</h3>
        <p className="text-sm text-muted-foreground mb-4">Utilisateurs actifs marqués « employé » et portant le tag « sap » dans Dolibarr (Utilisateurs › Tags/catégories). Seuls eux apparaissent dans l'application et peuvent se connecter. La colonne Métier reprend leurs autres tags Dolibarr (clean, green, DIY…). Ils se connectent avec leurs identifiants Dolibarr ; « Session Dolibarr active » indique que leurs pointages alimentent MySchedule en ce moment.</p>
        {overview.employees.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun employé trouvé (vérifiez la connexion Dolibarr).</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="employees-table">
              <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-2 pr-3 font-medium">Nom</th><th className="py-2 pr-3 font-medium">Identifiant</th><th className="py-2 pr-3 font-medium">Poste</th><th className="py-2 pr-3 font-medium">Métier</th><th className="py-2 pr-3 font-medium">Session</th><th className="py-2 pr-3 font-medium">Dernière connexion</th><th className="py-2" /></tr></thead>
              <tbody>
                {overview.employees.map((e) => (
                  <tr key={e.id} className="border-b border-border/60" data-testid="employee-row">
                    <td className="py-2 pr-3 font-medium whitespace-nowrap">{e.name}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{e.login}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{e.job}</td>
                    <td className="py-2 pr-3" data-testid="employee-tags">
                      {(e.tags || []).length ? (
                        <span className="flex flex-wrap gap-1">{e.tags.map((t) => <Badge key={t} variant="outline" className={`text-[11px] font-medium ${tagStyle(t).badge}`}>{t}</Badge>)}</span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 pr-3">
                      {e.revoked ? <Badge variant="destructive" data-testid="employee-revoked-badge">Accès révoqué</Badge>
                        : e.web_session ? <Badge className="bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--brand-mint))]" data-testid="employee-session-badge">Session Dolibarr active</Badge>
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">{e.last_login ? formatDate(e.last_login, true) : '—'}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant={e.revoked ? 'default' : 'ghost'} className="ml-1" disabled={accessBusy === e.login} onClick={() => toggleAccess(e)} data-testid={e.revoked ? 'employee-restore-access' : 'employee-revoke-access'}>
                        {accessBusy === e.login ? <Loader2 className="h-4 w-4 animate-spin" /> : e.revoked ? <><ShieldCheck className="h-4 w-4 mr-1" /> Rétablir</> : <><ShieldOff className="h-4 w-4 mr-1" /> Révoquer</>}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AdminTeamHours adminKey={adminKey} />

      {/* Notes de frais */}
      <Card className="p-5">
        <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2 mb-1"><Receipt className="h-4 w-4" /> Notes de frais saisies depuis l'espace Easer (90 jours)</h3>
        <p className="text-sm text-muted-foreground mb-4">Chaque note est créée dans Dolibarr (module Notes de frais) avec les tickets scannés en pièces jointes. Approuvez, refusez et remboursez depuis Dolibarr : le statut se met à jour ici et chez l'Easer.</p>
        {expenses === null ? <Skeleton className="h-16" /> : expenses.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="expenses-admin-empty">Aucune note de frais saisie depuis le site.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="expenses-admin-table">
              <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-2 pr-3 font-medium">Date</th><th className="py-2 pr-3 font-medium">Easer</th><th className="py-2 pr-3 font-medium">Dépense</th><th className="py-2 pr-3 font-medium">Réf.</th><th className="py-2 pr-3 font-medium text-right">TTC</th><th className="py-2 pr-3 font-medium">Statut</th><th className="py-2 font-medium">Tickets</th></tr></thead>
              <tbody>
                {expenses.map((e) => (
                  <tr key={e.id} className="border-b border-border/60" data-testid="expenses-admin-row">
                    <td className="py-2 pr-3 whitespace-nowrap">{e.date ? formatDate(e.date) : '—'}</td>
                    <td className="py-2 pr-3 font-medium whitespace-nowrap">{e.user_name}</td>
                    <td className="py-2 pr-3">{e.type_label}{e.comment ? <span className="text-muted-foreground"> · {e.comment}</span> : ''}</td>
                    <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{e.ref}</td>
                    <td className="py-2 pr-3 text-right tabular-nums font-semibold whitespace-nowrap">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(e.total_ttc || 0)}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <Badge className={`${e.status === 'submitted' ? 'bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]' : e.status === 'paid' ? 'bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]' : e.status === 'approved' ? 'bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage))]' : e.status === 'refused' ? 'bg-destructive text-destructive-foreground hover:bg-destructive' : ''}`} variant={['submitted', 'paid', 'approved', 'refused'].includes(e.status) ? 'default' : 'outline'}>{e.status_label}</Badge>
                    </td>
                    <td className="py-2">
                      <div className="flex gap-1">
                        {e.photos.map((p) => (
                          <a key={p.index} href={`${BACKEND_URL}/api/admin/easer/expenses/${e.local_id}/photos/${p.index}?key=${encodeURIComponent(adminKey)}`} target="_blank" rel="noopener noreferrer" data-testid="expense-admin-photo">
                            <img src={p.thumb} alt="Ticket" className="h-10 w-10 object-cover rounded border border-border" />
                          </a>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Comptes-rendus terrain */}
      <Card className="p-5">
        <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2 mb-1"><Camera className="h-4 w-4" /> Comptes-rendus et photos (30 derniers jours)</h3>
        <p className="text-sm text-muted-foreground mb-4">Envoyés par les Easers depuis le planning. Les photos sont aussi déposées sur la fiche d'intervention Dolibarr.</p>
        {reports === null ? <Skeleton className="h-16" /> : reports.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="reports-empty">Aucun compte-rendu pour le moment.</p>
        ) : (
          <ul className="divide-y divide-border" data-testid="reports-list">
            {reports.map((r) => (
              <li key={r.id} className="py-3 text-sm" data-testid="report-item">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-muted-foreground tabular-nums whitespace-nowrap">{formatDate(r.created_at, true)}</span>
                  <span className="font-medium">{r.user_name}</span>
                  <span className="text-muted-foreground">{r.label}{r.client_name ? ` · ${r.client_name}` : ''}</span>
                  {r.fichinter_ref && <Badge variant="outline" className="font-mono">{r.fichinter_ref}</Badge>}
                  <span className="text-xs text-muted-foreground ml-auto">{r.uploaded_count}/{r.photos.length} photo{r.photos.length > 1 ? 's' : ''} sur Dolibarr</span>
                </div>
                {r.note && <p className="mt-1">{r.note}</p>}
                {r.photos.length > 0 && (
                  <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                    {r.photos.map((p) => (
                      <a key={p.index} href={`${BACKEND_URL}/api/admin/easer/reports/${r.id}/photos/${p.index}?key=${encodeURIComponent(adminKey)}`} target="_blank" rel="noopener noreferrer" className="relative flex-shrink-0" data-testid="report-photo-link">
                        <img src={p.thumb} alt={`Photo ${p.kind_label}`} className="h-20 w-20 object-cover rounded-lg border border-border" />
                        <span className="absolute bottom-1 left-1 rounded bg-black/60 text-white text-[10px] px-1">{p.kind_label}</span>
                      </a>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <AdminPointageAlerts adminKey={adminKey} />

      {/* Activité */}
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2"><Activity className="h-4 w-4" /> Activité des 14 derniers jours</h3>
          <div className="flex gap-1">
            {[['all', 'Tout'], ['incident', 'Imprévus'], ['pointage', 'Pointages'], ['compte_rendu', 'Comptes-rendus']].map(([id, label]) => (
              <Button key={id} size="sm" variant={activityFilter === id ? 'default' : 'outline'} onClick={() => setActivityFilter(id)} data-testid={`activity-filter-${id}`}>{label}</Button>
            ))}
          </div>
        </div>
        {activity === null ? <Skeleton className="h-16" /> : filteredActivity.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="activity-empty">Aucune activité dans cette catégorie.</p>
        ) : (
          <ul className="divide-y divide-border max-h-96 overflow-y-auto" data-testid="activity-list">
            {filteredActivity.map((a, i) => (
              <li key={`${a.created_at}-${i}`} className={`py-2 flex items-center gap-3 text-sm ${a.kind === 'incident' ? 'bg-[hsl(var(--accent))]/5 -mx-2 px-2 rounded' : ''}`} data-testid={a.kind === 'incident' ? 'activity-incident' : undefined}>
                <span className="text-muted-foreground tabular-nums whitespace-nowrap w-32">{formatDate(a.created_at, true)}</span>
                <span className="font-medium">{a.user_name}</span>
                <span className="text-muted-foreground flex-1">
                  {a.kind === 'incident' ? (
                    <><AlertTriangle className="inline h-3.5 w-3.5 mr-1 text-[hsl(var(--accent))]" /><strong className="text-foreground">{INCIDENT_LABELS[a.details?.kind] || 'Imprévu'}{a.details?.delay_min ? ` (~${a.details.delay_min} min)` : ''}</strong>{a.details?.client_name ? ` · ${a.details.client_name}` : ''}{a.details?.note ? ` — ${a.details.note}` : ''}</>
                  ) : (
                    <>{KIND_LABELS[a.kind] || a.kind}{a.details?.event_id ? ` · événement #${a.details.event_id}` : ''}{a.details?.fichinter_ref ? ` · ${a.details.fichinter_ref}` : ''}</>
                  )}
                </span>
                {a.kind === 'intervention_terminee' && (
                  <a href={`${BACKEND_URL}/api/admin/easer/signatures/${a.details.event_id}?key=${encodeURIComponent(adminKey)}`} target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] inline-flex items-center gap-1" data-testid="activity-signature-link"><PenLine className="h-3.5 w-3.5" /> Signature</a>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <DecisionDialog decision={decision} adminKey={adminKey} onClose={() => setDecision(null)} onSaved={() => { setDecision(null); load(); }} />
    </div>
  );
}


function DecisionDialog({ decision, adminKey, onClose, onSaved }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNote(''); }, [decision]);
  if (!decision) return null;
  const approve = decision.status === 'approved';

  const save = async () => {
    setSaving(true);
    try {
      await adminPost(`/api/admin/easer/leaves/${decision.leave.id}/decision`, adminKey, {}, { status: decision.status, note });
      toast.success(approve ? 'Demande acceptée' : 'Demande refusée');
      onSaved();
    } catch (err) {
      reportError('décision congés', err, (m) => toast.error(m || 'Échec'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Space_Grotesk']">{approve ? 'Accepter' : 'Refuser'} la demande</DialogTitle>
          <DialogDescription>{decision.leave.user_name} · {decision.leave.type_label} · du {formatDate(decision.leave.start)} au {formatDate(decision.leave.end)}. L'événement agenda Dolibarr sera mis à jour.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="decision-note">Message pour l'employé (optionnel)</Label>
          <Textarea id="decision-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="decision-note" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button onClick={save} disabled={saving} className={approve ? 'bg-[hsl(var(--brand-sage))] hover:bg-[hsl(var(--brand-sage))]/90 text-white' : ''} variant={approve ? 'default' : 'destructive'} data-testid="decision-confirm">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Confirmer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
